import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  useUploadFile,
  useIngestionJobs,
  useIngestionJob,
  useFinalizeIngestion,
  useCancelIngestion,
} from '@/hooks/useIngestion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatBytes, formatDate } from '@/lib/utils';
import { SkeletonTable } from '@/components/skeletons/SkeletonLoaders';
import { EmptyIngestionJobsState, ErrorState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/hooks/useToast';
import type { IngestionJob, PaginatedResponse } from '@/types';

export const Ingestion = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [jobToCancel, setJobToCancel] = useState<string | null>(null);

  const uploadFile = useUploadFile();
  const { data: jobs, isLoading: jobsLoading, error: jobsError, refetch } = useIngestionJobs({ limit: 50 });
  const { data: activeJob } = useIngestionJob(activeJobId || '');
  const finalizeIngestion = useFinalizeIngestion();
  const cancelIngestion = useCancelIngestion();
  const { toast } = useToast();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/json': ['.json'],
      'application/x-parquet': ['.parquet'],
    },
    maxFiles: 1,
  });

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      const result = await uploadFile.mutateAsync({
        file: selectedFile,
        onProgress: setUploadProgress,
      });
      setActiveJobId(result.job_id);
      setSelectedFile(null);
      setUploadProgress(0);
      toast({
        title: 'Upload successful',
        description: `File "${selectedFile.name}" uploaded and processing started`,
      });
    } catch (err: unknown) {
      const error = err as Error;
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload file',
        variant: 'destructive',
      });
    }
  };

  const handleFinalize = async (jobId: string) => {
    try {
      await finalizeIngestion.mutateAsync(jobId);
      toast({
        title: 'Ingestion finalized',
        description: 'Data has been successfully registered in the lakehouse',
      });
      setActiveJobId(null);
    } catch (err: unknown) {
      const error = err as Error;
      toast({
        title: 'Finalization failed',
        description: error.message || 'Failed to finalize ingestion',
        variant: 'destructive',
      });
    }
  };

  const handleCancelClick = (jobId: string) => {
    setJobToCancel(jobId);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!jobToCancel) return;

    try {
      await cancelIngestion.mutateAsync(jobToCancel);
      toast({
        title: 'Ingestion cancelled',
        description: 'The ingestion job has been cancelled',
      });
      if (activeJobId === jobToCancel) {
        setActiveJobId(null);
      }
      setCancelDialogOpen(false);
      setJobToCancel(null);
    } catch (err: unknown) {
      const error = err as Error;
      toast({
        title: 'Cancellation failed',
        description: error.message || 'Failed to cancel ingestion',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Data Ingestion</h1>
        <p className="text-muted-foreground">
          Upload and process data files into the lakehouse
        </p>
      </div>

      {/* File Upload */}
      <Card>
        <CardHeader>
          <CardTitle>Upload Data File</CardTitle>
          <CardDescription>
            Drag and drop a file or click to browse. Supported formats: CSV, JSON, Parquet
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
          >
            <input {...getInputProps()} />
            {selectedFile ? (
              <div>
                <p className="font-medium">{selectedFile.name}</p>
                <p className="text-sm text-muted-foreground">
                  {formatBytes(selectedFile.size)}
                </p>
              </div>
            ) : isDragActive ? (
              <p>Drop the file here...</p>
            ) : (
              <div>
                <p className="mb-2">Drag & drop a data file here</p>
                <p className="text-sm text-muted-foreground">or click to select a file</p>
              </div>
            )}
          </div>

          {selectedFile && (
            <div className="flex gap-2">
              <Button onClick={handleUpload} disabled={uploadFile.isPending}>
                {uploadFile.isPending ? `Uploading... ${uploadProgress}%` : 'Upload & Process'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setSelectedFile(null)}
                disabled={uploadFile.isPending}
              >
                Clear
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Job Status */}
      {activeJob && (
        <Card>
          <CardHeader>
            <CardTitle>Current Ingestion</CardTitle>
            <CardDescription>Processing: {activeJob.filename}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm">Status:</span>
              <span
                className={`text-xs px-2 py-1 rounded ${
                  activeJob.status === 'completed'
                    ? 'bg-success/10 text-success'
                    : activeJob.status === 'processing'
                    ? 'bg-info/10 text-info'
                    : activeJob.status === 'failed'
                    ? 'bg-destructive/10 text-destructive'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {activeJob.status}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Current Step:</span>
              <span className="text-sm font-medium">{activeJob.current_step || 'N/A'}</span>
            </div>
            {activeJob.status === 'completed' && (
              <Button onClick={() => handleFinalize(activeJob.id)} disabled={finalizeIngestion.isPending}>
                Finalize Ingestion
              </Button>
            )}
            {(activeJob.status === 'processing' || activeJob.status === 'pending') && (
              <Button
                variant="destructive"
                onClick={() => handleCancelClick(activeJob.id)}
                disabled={cancelIngestion.isPending}
              >
                Cancel Ingestion
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Job History */}
      <Card>
        <CardHeader>
          <CardTitle>Ingestion History</CardTitle>
          <CardDescription>Recent ingestion jobs</CardDescription>
        </CardHeader>
        <CardContent>
          {jobsLoading ? (
            <SkeletonTable rows={5} />
          ) : jobsError ? (
            <ErrorState
              message={jobsError instanceof Error ? jobsError.message : 'Failed to load ingestion jobs'}
              onRetry={() => refetch()}
            />
          ) : jobs?.items?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium">File</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Status</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Created</th>
                    <th className="px-4 py-2 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(jobs as PaginatedResponse<IngestionJob>).items.map((job) => (
                    <tr key={job.id} className="hover:bg-muted/50">
                      <td className="px-4 py-2 text-sm">{job.filename || 'N/A'}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`text-xs px-2 py-1 rounded whitespace-nowrap ${
                            job.status === 'completed'
                              ? 'bg-success/10 text-success'
                              : job.status === 'processing'
                              ? 'bg-info/10 text-info'
                              : job.status === 'failed'
                              ? 'bg-destructive/10 text-destructive'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {job.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {job.created_at ? formatDate(job.created_at) : 'N/A'}
                      </td>
                      <td className="px-4 py-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setActiveJobId(job.id)}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyIngestionJobsState onUpload={() => {/* File dropzone is above */}} />
          )}
        </CardContent>
      </Card>

      {/* Confirm Cancel Dialog */}
      <ConfirmDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        title="Cancel Ingestion"
        description="Are you sure you want to cancel this ingestion job? This action cannot be undone."
        onConfirm={handleCancelConfirm}
        confirmText="Cancel Ingestion"
        variant="destructive"
        loading={cancelIngestion.isPending}
      />
    </div>
  );
};
